import React, { createContext, useContext, useState, useEffect } from 'react';

const CommunicationTemplatesContext = createContext();

export const useCommunicationTemplates = () => {
  const context = useContext(CommunicationTemplatesContext);
  if (!context) {
    throw new Error('useCommunicationTemplates must be used within a CommunicationTemplatesProvider');
  }
  return context;
};

export const CommunicationTemplatesProvider = ({ children }) => {
  const [templates, setTemplates] = useState(() => {
    try {
      const storedTemplates = localStorage.getItem('CommunicationTemplates');
      if (!storedTemplates) {
        return [];
      }
      const parsedTemplates = JSON.parse(storedTemplates);
      return Array.isArray(parsedTemplates) ? parsedTemplates : [];
    } catch (error) {
      console.error('Error loading templates from localStorage:', error);
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('CommunicationTemplates', JSON.stringify(templates));
    } catch (error) {
      console.error('Error saving templates to localStorage:', error);
    }
  }, [templates]);

  const addTemplate = async (template) => {
    try {
      const newTemplate = {
        ...template,
        id: Date.now().toString(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        isActive: true
      };
      setTemplates(prev => [...prev, newTemplate]);
      return newTemplate;
    } catch (error) {
      console.error('Error adding template:', error);
      throw error;
    }
  };

  const getTemplate = (id) => {
    return templates.find(template => template.id === id);
  };

  const updateTemplate = async (id, updatedTemplate) => {
    try {
      setTemplates(prev => prev.map(template =>
        template.id === id ? {
          ...template,
          ...updatedTemplate,
          updatedAt: new Date().toISOString()
        } : template
      ));
    } catch (error) {
      console.error('Error updating template:', error);
      throw error;
    }
  };

  const deleteTemplate = async (id) => {
    try {
      setTemplates(prev => prev.filter(template => template.id !== id));
    } catch (error) {
      console.error('Error deleting template:', error);
      throw error;
    }
  };

  return (
    <CommunicationTemplatesContext.Provider
      value={{
        templates,
        addTemplate,
        getTemplate,
        updateTemplate,
        deleteTemplate,
      }}
    >
      {children}
    </CommunicationTemplatesContext.Provider>
  );
}; 